import { type FC, useCallback, useMemo, useState } from "react";

import {
  Badge,
  Box,
  Button,
  Column,
  MeetingIcon,
  Row,
  SearchInput,
  Text,
  ToggleButton,
  ToggleButtonGroup,
  TextWithTooltip,
  EmptyState,
} from "@hightouch/htui";
import noop from "lodash/noop";
import omit from "lodash/omit";
import { isColumnReferenceEqual } from "src/components/explore/filter-popover/utils";
import round from "lodash/round";
import { useFormContext } from "react-hook-form";
import EmptySearchSrc from "src/assets/placeholders/search.svg";
import { captureException } from "src/lib/monitoring";
import { useNavigate } from "src/router";
import { isPresent } from "ts-extras";

import { DateRangePicker } from "src/components/analytics/date-range-picker";
import type { FunnelMetricDataForCohort } from "src/graphql";
import * as analytics from "src/lib/analytics";
import { FunnelsNoDataPlaceholder } from "src/pages/analytics/placeholders";
import { useAnalyticsContext } from "src/pages/analytics/state";
import { LookbackOptions } from "src/pages/analytics/state/constants";
import {
  type ChartFormState,
  type FunnelStepGraphData,
  TimeOptions,
} from "src/pages/analytics/types";
import {
  createEventConditionFromFunnelSteps,
  EMPTY_AUDIENCE_NAME,
  formatDatePickerLabel,
  getTableDataFromFunnelData,
  transformFunnelDataForGraph,
} from "src/pages/analytics/utils";
import type { CreateAudienceState } from "src/pages/audiences/types";
import {
  type ColumnType,
  createPropertyCondition,
  type EventCondition,
  type PropertyCondition,
} from "src/types/visual";
import { Table } from "src/ui/table";

import {
  type ClickFunnelBarHandler,
  FunnelGraph,
} from "src/components/analytics/funnel/funnel-graph";
import { encode } from "src/hooks/use-encoded-search-params";
import { FunnelChartDropdown } from "./funnel-chart-dropdown";
import { TableChartLoader } from "src/components/charts/loaders";
import { DEFAULT_CHART_HEIGHT } from "src/components/charts/chart";
import funnelsPlaceholder from "src/assets/placeholders/funnels.svg";
import { LastRefreshedAtLabel } from "../shared/controls/last-refreshed-at";
import { RefreshControl } from "../shared/controls/refresh-control";

type Props = {
  data: FunnelMetricDataForCohort[];
  hasErrors?: boolean;
  isLoading?: boolean;
};

export const FunnelChart: FC<Props> = ({
  data,
  hasErrors = false,
  isLoading = false,
}) => {
  const navigate = useNavigate();
  const { events, parent, setLookbackWindow, setSelectedDates } =
    useAnalyticsContext();

  const form = useFormContext<ChartFormState>();

  // biome-ignore lint/suspicious/noTsIgnore: legacy suppression
  /* @ts-ignore - Circular reference problem */
  const parentModelId = form.watch("parentModelId");
  const groupByColumns = form.watch("groupByColumns");

  // biome-ignore lint/suspicious/noTsIgnore: legacy suppression
  /* @ts-ignore - Circular reference problem */
  const selectedAudiences = form.watch("selectedAudiences");
  const selectedDateStrings = form.watch("selectedDates");
  const timeValue = form.watch("timeValue");

  const funnelsGraphData = useMemo(
    () =>
      transformFunnelDataForGraph({
        audiences: selectedAudiences ?? [],
        data,
        events,
      }),
    [events, data, selectedAudiences],
  );

  const [selectedConditions, setSelectedConditions] = useState<
    (EventCondition | PropertyCondition)[]
  >([]);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [search, setSearch] = useState("");

  const selectedDates = useMemo(
    () => selectedDateStrings.map((dateStr) => new Date(dateStr)),
    [selectedDateStrings?.[0], selectedDateStrings?.[1]],
  );

  const tableData = getTableDataFromFunnelData(funnelsGraphData);
  const filteredTableData = tableData.filter(({ seriesName }) => {
    return seriesName.toLowerCase().includes(search.toLowerCase());
  });
  const hasRealAudience = filteredTableData.some(
    (row) => row.seriesName !== EMPTY_AUDIENCE_NAME,
  );

  const openCreateAudienceDropdown = useCallback(
    (
      stage: FunnelStepGraphData,
      groupByValue?: string | number,
      didPerform = true,
    ) => {
      // need a way to figure out first and second step
      if (!stage.eventModelId || !stage.relationshipId) {
        captureException(new Error("[Funnels] Stage data not found"));
        return;
      }

      const conditions: (EventCondition | PropertyCondition)[] = [];

      const propertyConditions: PropertyCondition[] = groupByColumns
        .filter(isPresent)
        .map((columnReference) => {
          const column = parent?.filterable_audience_columns.find(
            ({ column_reference }) =>
              // parentModelId is used to determine merge columns on the frontend
              // but it's not in column_reference
              isColumnReferenceEqual(
                column_reference,
                omit(columnReference, "parentModelId"),
              ),
          );

          return {
            ...createPropertyCondition(),
            propertyType: (column?.type ?? null) as ColumnType | null,
            property: column?.column_reference,
            operator: "=",
            value: [groupByValue],
          };
        })
        .filter(isPresent);

      if (stage.index > 0) {
        const previousStep = funnelsGraphData[stage.index - 1];
        if (!previousStep) {
          captureException(
            new Error("[Funnels] Previous stage's data not found"),
          );
          return;
        }

        conditions.push(
          createEventConditionFromFunnelSteps({
            stage: {
              eventModelId: previousStep.eventModelId,
              relationshipId: previousStep.relationshipId,
              subconditions: previousStep.subconditions,
            },
            secondStage: {
              eventModelId: stage.eventModelId,
              relationshipId: stage.relationshipId,
              subconditions: stage.subconditions,
            },
            didPerform,
          }),
        );
      } else {
        conditions.push(
          createEventConditionFromFunnelSteps({
            stage: {
              eventModelId: stage.eventModelId,
              relationshipId: stage.relationshipId,
              subconditions: stage.subconditions,
            },
            didPerform,
          }),
        );
      }

      conditions.push(...propertyConditions);

      setSelectedConditions(conditions);
    },
    [funnelsGraphData, groupByColumns, parent?.filterable_audience_columns],
  );

  const closeAudienceDropdown = useCallback(() => {
    setSelectedConditions([]);
  }, []);

  const createAudience = useCallback(() => {
    if (selectedConditions.length === 0 || !parentModelId) {
      return;
    }

    analytics.track("Creating Audience from Funnel Chart", {
      parent_model_id: parentModelId,
      parent_model_name: parent?.name,
      parent_model_source_name: parent?.connection?.name,
      parent_model_source_type: parent?.connection?.definition.name,
      number_of_conditions: selectedConditions.length,
    });

    const state: CreateAudienceState = {
      referrer: {
        pathname: "analytics",
        search: location.search,
      },
      // bigint -> Type says string but it's actually a number
      parentModelId: parentModelId as unknown as string,
      conditions: selectedConditions,
    };

    const encodedState = encode(state);

    navigate(`/audiences/new?state=${encodedState}`);
  }, [navigate, parent, parentModelId, selectedConditions]);

  const onClickBar: ClickFunnelBarHandler = useCallback(
    ({ payload, event, seriesName, didPerform = true }) => {
      setPopoverPosition({
        x: event.clientX,
        y: event.clientY,
      });

      openCreateAudienceDropdown(
        payload,
        payload.data[seriesName]?.groupByValue,
        didPerform,
      );

      analytics.track("Clicked on Funnel Bar");
    },
    [openCreateAudienceDropdown],
  );
  const showPlaceholder =
    !isLoading && (!funnelsGraphData || funnelsGraphData.length === 0);

  if (!isLoading && hasErrors) {
    return (
      <EmptyState
        variant="minimal"
        flex={1}
        imageUrl={funnelsPlaceholder}
        title="Error running query"
        message="There was an error running this query. Check the errors shown in the sidebar and try again."
      />
    );
  }

  return (
    <>
      <Row align="center" justify="space-between" gap={2} px={6} py={4}>
        <Row gap={2}>
          <ToggleButtonGroup
            size="md"
            value={timeValue}
            onChange={(value) => setLookbackWindow(value as TimeOptions)}
          >
            {LookbackOptions.map((option) => (
              <ToggleButton key={option.value} {...option}></ToggleButton>
            ))}
          </ToggleButtonGroup>

          <DateRangePicker
            maxDate={new Date()}
            selectedDates={selectedDates}
            onChange={(dates) => {
              setSelectedDates(dates);
            }}
          >
            <Box
              as={Button}
              background={
                timeValue === TimeOptions.Custom ? "gray.200" : "unset"
              }
              fontWeight={
                timeValue === TimeOptions.Custom ? "semibold" : "normal"
              }
              icon={MeetingIcon}
              size="md"
              onClick={noop}
            >
              {formatDatePickerLabel(selectedDates, timeValue)}
            </Box>
          </DateRangePicker>
        </Row>
        <Row align="center" gap={2}>
          <LastRefreshedAtLabel />
          <RefreshControl parentModelId={parentModelId} />
        </Row>
      </Row>
      {showPlaceholder ? (
        <FunnelsNoDataPlaceholder parentModelId={parentModelId} />
      ) : (
        <>
          {/**
           * Using the classic aspect ratio trick to get responsiveness working.
           * Without these wrapper divs, the chart has a hard time resizing itself to use the appropriate amount of space.
           */}
          <Box px={6} flex={isLoading ? 0 : 1}>
            <FunnelGraph
              isLoading={isLoading}
              height={DEFAULT_CHART_HEIGHT}
              graphData={funnelsGraphData}
              showLegend={hasRealAudience}
              onClickBar={onClickBar}
            />
          </Box>
          <Box
            mb={4}
            borderBottomWidth="hairline"
            borderBottomStyle="solid"
            borderColor="base.divider"
          />
          {(isLoading || funnelsGraphData.length > 0) && (
            <Column flex={1}>
              {!isLoading && (
                // Hide when loading - TableChartLoader below will handle this.
                <Row align="center" justify="space-between" px={6} pb={4}>
                  <Row align="center" gap={2}>
                    <Text fontWeight="medium" size="lg">
                      All series
                    </Text>
                    <Badge>{tableData.length}</Badge>
                  </Row>

                  <SearchInput
                    placeholder="Search series…"
                    width="xs"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </Row>
              )}

              {/**
               * Using `TableChartLoader` instead of the Table loading state so it can be a bit more precise and constrained here.
               *
               * @taylor in the future, use dynamic column count if possible -> https://github.com/hightouchio/hightouch/pull/27163/files#r2413882468
               *   column = steps x segments x (values in the group column(s))
               *       the # of steps selected + the # of segments.
               *        ignore groups because that gets crazy. it's steps x segments x (values in the group column(s))
               */}
              {isLoading ? (
                <TableChartLoader rows={3} />
              ) : (
                <Table
                  scrollable
                  scrollableY
                  data={filteredTableData}
                  headerHeight="10"
                  placeholder={{
                    image: EmptySearchSrc,
                    title: "No results found",
                    body: "Please update your search and try again.",
                  }}
                  columns={[
                    ...(hasRealAudience
                      ? [
                          {
                            name: "Audience",
                            cell: ({ seriesName }) => (
                              <TextWithTooltip color="text.secondary" size="sm">
                                {seriesName}
                              </TextWithTooltip>
                            ),
                          },
                        ]
                      : []),
                    {
                      name: "Conversion rate",
                      cell: ({ conversion }) => (
                        <Text color="text.secondary" size="sm">
                          {round(conversion * 100, 2)}%
                        </Text>
                      ),
                    },
                    ...(funnelsGraphData ?? [])
                      .map(({ stageName }, index) => ({
                        header: () => {
                          const headerName = `${index + 1}. ${stageName}`;

                          return (
                            <TextWithTooltip
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              {headerName}
                            </TextWithTooltip>
                          );
                        },
                        cell: ({ steps }) => {
                          const step = steps[index];

                          return (
                            <Text
                              color="text.secondary"
                              size="sm"
                              variant="numeric"
                            >
                              {step.numberOfUsers.toLocaleString()} (
                              {round(step.conversion * 100, 2)}%)
                            </Text>
                          );
                        },
                      }))
                      .filter(isPresent),
                  ]}
                />
              )}
            </Column>
          )}
        </>
      )}

      <FunnelChartDropdown
        isOpen={selectedConditions.length > 0}
        label="Create audience"
        position={popoverPosition}
        onClick={createAudience}
        onClose={closeAudienceDropdown}
      />
    </>
  );
};
